import { supabase } from '../lib/supabaseClient';
import type { BranchIncomeRecord, ExpenseRecord } from '../types/db';

/**
 * Fetches all income records for the current user's scope.
 */
export async function fetchBranchIncomes(): Promise<BranchIncomeRecord[]> {
  const { data, error } = await supabase
    .from('branch_incomes')
    .select('*')
    .order('date', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/**
 * Fetches all expense records for the current user's scope.
 */
export async function fetchExpenses(): Promise<ExpenseRecord[]> {
  const { data, error } = await supabase
    .from('expenses')
    .select('*')
    .order('date', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

/**
 * Creates a new income record.
 */
export async function createIncome(payload: {
  description: string;
  amount: number;
  date: string;
  branch_id: string;
}) {
  const { error } = await supabase.from('branch_incomes').insert(payload);

  if (error) throw error;
}

/**
 * Approves a pending student payment.
 */
export async function approvePayment(id: string) {
  const { data, error } = await supabase.functions.invoke('approve-payment', {
    body: { payment_id: id },
  });
  if (error) throw error;
  return data;
}

/**
 * Rejects a pending student payment.
 */
export async function rejectPayment(id: string) {
  const { error } = await supabase
    .from('pending_payments')
    .update({ status: 'REJECTED' })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Approves an online registration with custom credentials.
 */
export async function approveRegistration(payload: {
  registration_id: string;
  email?: string;
  password?: string;
}) {
  const { data, error } = await supabase.functions.invoke('approve-online-registration', {
    body: payload,
  });
  if (error) throw error;
  return data;
}

/**
 * Rejects an online registration.
 */
export async function rejectRegistration(id: string) {
  const { error } = await supabase
    .from('online_registrations')
    .update({ status: 'REJECTED' })
    .eq('id', id);
  if (error) throw error;
}

/**
 * PANGGIL EDGE FUNCTION: calculate-tentor-honor
 */
export async function calculateTentorHonor(tentor_id: string, period: string) {
  const { data, error } = await supabase.functions.invoke('calculate-tentor-honor', {
    body: { tentor_id, period },
  });

  if (error) throw error;
  return data as { totalSessions: number; totalHonor: number };
}

/**
 * 🏛️ PAYROLL SYSTEM CHAIN-OF-DATES
 */

export async function getLastCutoff(branchId: string): Promise<string | null> {
  const { data, error } = await supabase.rpc('get_last_cutoff', { p_branch_id: branchId });
  if (error) throw error;
  return data;
}

export async function runPayrollGeneration(branchId: string, period: string, cutoffDate: string) {
  const { error } = await supabase.rpc('generate_payroll_with_cutoff', {
    p_branch_id: branchId,
    p_period: period,
    p_cutoff_date: cutoffDate,
  });
  if (error) throw error;
}
